/**
 * Jednorazowy parametr `state` dla przeplywow OAuth, ktore wracaja na strone frontu.
 *
 * Przeplywy wracajace na funkcje brzegowa (np. linkedin-social-oauth) nie moga z tego korzystac,
 * bo funkcja nie widzi sessionStorage przegladarki - tam state jest podpisywany po stronie serwera.
 */

const STATE_TTL_MS = 10 * 60 * 1000;

interface StoredState {
  value: string;
  expiresAt: number;
}

function storageKey(provider: string): string {
  return `oauth_state:${provider}`;
}

/** Losuje state i zapamietuje go do czasu powrotu z autoryzacji. */
export function createOAuthState(provider: string): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const value = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

  const stored: StoredState = { value, expiresAt: Date.now() + STATE_TTL_MS };
  sessionStorage.setItem(storageKey(provider), JSON.stringify(stored));

  return value;
}

/**
 * Sprawdza state z powrotnego przekierowania i od razu go zuzywa, zeby nie dalo sie go powtorzyc.
 * Rzuca wyjatkiem, gdy state nie pasuje - wtedy kodu autoryzacyjnego nie wolno wymieniac na token.
 */
export function consumeOAuthState(provider: string, received: string | null): void {
  const key = storageKey(provider);
  const raw = sessionStorage.getItem(key);
  sessionStorage.removeItem(key);

  const retry = 'Zacznij laczenie konta od nowa.';

  if (!raw) {
    throw new Error(`Nie znalazlem rozpoczetej autoryzacji w tej przegladarce. ${retry}`);
  }

  let stored: StoredState;
  try {
    stored = JSON.parse(raw) as StoredState;
  } catch {
    throw new Error(`Zapisana autoryzacja jest uszkodzona. ${retry}`);
  }

  if (Date.now() > stored.expiresAt) {
    throw new Error(`Autoryzacja wygasla (ponad 10 minut). ${retry}`);
  }

  if (!received || received !== stored.value) {
    throw new Error(`Parametr state nie zgadza sie z rozpoczeta autoryzacja - przerwano dla bezpieczenstwa. ${retry}`);
  }
}
